import React, { useState } from 'react';
import { Plus, Minus, CreditCard } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { auth } from '../firebase';
import { cn } from '../util/designUtil';
import { formatNumber } from '../util/formatters';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn('w-full bg-white rounded-xl shadow-lg border border-gray-200', className)}>{children}</div>
);
const CardHeader: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn('p-4 sm:p-6', className)}>{children}</div>
);
const CardTitle: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <h2 className={cn('text-xl font-semibold text-gray-900', className)}>{children}</h2>
);
const CardDescription: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <p className={cn('text-sm text-gray-500 mt-1', className)}>{children}</p>
);
const CardContent: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn('p-4 sm:p-6 space-y-6', className)}>{children}</div>
);
const CardFooter: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn('p-4 sm:p-6 flex flex-col border-t border-gray-200', className)}>{children}</div>
);

const Button: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: 'default' | 'outline' | 'ghost';
    size?: 'icon' | 'default';
  }
> = ({ children, className, variant, size, ...props }) => {
  const baseStyle =
    'font-medium rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2';
  let variantStyle = '';
  if (variant === 'ghost') {
    variantStyle = 'hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50';
    if (size === 'icon') variantStyle += ' p-2';
    else variantStyle += ' px-4 py-2';
  } else if (variant === 'outline') {
    variantStyle = 'border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 text-sm';
  } else {
    variantStyle = 'bg-indigo-600 text-white hover:bg-indigo-700 px-4 py-2 text-sm disabled:opacity-70';
  }
  return (
    <button className={cn(baseStyle, variantStyle, className)} {...props}>
      {children}
    </button>
  );
};

const BuyCreditsCreemScreen: React.FC = () => {
  const [amount, setAmount] = useState<number>(20);
  const [isProcessingPayment, setIsProcessingPayment] = useState<boolean>(false);
  const presetAmounts = [10, 20, 50, 100];
  const { user } = useAuth();

  const incrementAmount = () => setAmount((prev) => prev + 5);
  const decrementAmount = () => setAmount((prev) => Math.max(5, prev - 5));

  const handlePurchase = async () => {
    if (!user) {
      toast.error('Please log in to purchase credits.');
      return;
    }

    setIsProcessingPayment(true);

    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        toast.error('Authentication error. Please log in again.');
        setIsProcessingPayment(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/api/creem/checkout`,
        { amount },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );

      if (response.data.success && response.data.checkoutUrl) {
        toast.success('Redirecting to checkout...');
        window.location.href = response.data.checkoutUrl;
      } else {
        toast.error(response.data.error || 'Failed to create checkout session.');
        setIsProcessingPayment(false);
      }
    } catch (error: any) {
      console.error('Error creating checkout:', error);
      toast.error(error.response?.data?.message || error.message || 'Could not start payment process.');
      setIsProcessingPayment(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Buy Credits</CardTitle>
            <CreditCard className="text-indigo-500" size={28} />
          </div>
          <CardDescription>Top up your account balance. $1.00 = 1 Credit.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {presetAmounts.map((preset) => (
              <Button
                key={preset}
                variant={amount === preset ? 'default' : 'outline'}
                onClick={() => setAmount(preset)}
                className={cn(
                  amount === preset
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-100',
                )}
                disabled={isProcessingPayment}
              >
                ${preset}
              </Button>
            ))}
          </div>

          <div className="flex items-center justify-between border border-gray-300 rounded-md p-1">
            <Button variant="ghost" size="icon" onClick={decrementAmount} disabled={amount <= 5 || isProcessingPayment}>
              <Minus className="h-5 w-5 text-gray-600" />
            </Button>
            <span className="text-xl font-semibold text-gray-800">${amount.toFixed(2)}</span>
            <Button variant="ghost" size="icon" onClick={incrementAmount} disabled={isProcessingPayment}>
              <Plus className="h-5 w-5 text-gray-600" />
            </Button>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Current balance:</span>
              <span className="font-medium text-gray-800">
                {user?.credits !== undefined ? `${formatNumber(user.credits)} Credits` : 'Loading...'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">After top-up (approx):</span>
              <span className="font-medium text-gray-800">
                {user?.credits !== undefined
                  ? `${formatNumber(user.credits + amount)} Credits`
                  : `${formatNumber(amount)} Credits`}
              </span>
            </div>
          </div>
        </CardContent>

        <CardFooter>
          <Button
            onClick={handlePurchase}
            disabled={isProcessingPayment || amount < 5 || !user}
            className="w-full py-3 text-base"
          >
            {isProcessingPayment ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Redirecting to checkout...
              </span>
            ) : (
              `Top Up $${amount} (Get ${amount} Credits)`
            )}
          </Button>
          {!user && <p className="text-center text-sm text-red-500 mt-2">Please log in to purchase credits.</p>}
          <p className="text-xs text-gray-500 text-center mt-4">
            Payments are securely processed by Creem. Credits will be added after successful payment confirmation.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default BuyCreditsCreemScreen;
